import React, { useState, useEffect } from 'react';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';
import { Send, Bug, Lightbulb, HelpCircle, Clock, Loader2, Image as ImageIcon, Menu, MessageCircleQuestion, ChevronDown, Mail, MessageSquare, BookOpen, ExternalLink, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';

const TICKET_TYPES = [
  { id: 'bug', label: 'Bug', icon: Bug, color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-900/20' },
  { id: 'suggestion', label: 'Saran', icon: Lightbulb, color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-900/20' },
  { id: 'question', label: 'Tanya', icon: HelpCircle, color: 'text-blue-500', bg: 'bg-indigo-50 dark:bg-indigo-950/30' }
];

const STATUS_COLORS = {
  open: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  in_progress: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-400',
  resolved: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
};

const STATUS_LABELS = { open: 'Open', in_progress: 'In Progress', resolved: 'Resolved' };

const FAQ_ITEMS = [
  { q: 'Bagaimana cara menghubungkan WhatsApp?', a: 'Buka menu Channel di sidebar, klik "Hubungkan WhatsApp", lalu ikuti langkah-langkah verifikasi nomor bisnis Anda melalui Meta Business Suite.' },
  { q: 'Kenapa AI tidak menjawab pertanyaan stok?', a: 'Pastikan produk Anda sudah memiliki data varian dan stok yang lengkap di menu Produk. AI membaca data stok dari katalog produk Anda.' },
  { q: 'Bagaimana cara upgrade atau downgrade paket?', a: 'Buka Pengaturan > Langganan. Di sana Anda bisa melihat paket saat ini dan memilih upgrade (bayar) atau downgrade (dijadwalkan di akhir billing cycle).' },
  { q: 'Bagaimana cara verifikasi bukti pembayaran customer?', a: 'Ketika customer mengirim bukti transfer via WhatsApp, AI akan otomatis mendeteksi dan mengupload bukti tersebut. Status pesanan berubah ke "Verifikasi" untuk Anda konfirmasi manual.' },
  { q: 'Apakah data saya aman?', a: 'Ya, semua data dienkripsi dan disimpan di server yang aman. Kami menggunakan Google OAuth dan JWT token untuk autentikasi.' },
  { q: 'Bagaimana cara sinkronisasi ke Google Sheets?', a: 'Buka menu Google Sheets di sidebar, hubungkan akun Google Anda, lalu pilih spreadsheet tujuan. Data pesanan akan otomatis tersinkron.' },
];

export default function FeedbackPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [view, setView] = useState('form');
  const [type, setType] = useState('');
  const [message, setMessage] = useState('');
  const [screenshotUrl, setScreenshotUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [replyImageUrl, setReplyImageUrl] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);

  useEffect(() => { fetchTickets(); }, []);

  const fetchTickets = async () => {
    try {
      const res = await axios.get(`${API}/tickets`, { headers });
      setTickets(res.data.tickets || []);
    } catch (err) {}
  };

  const handleSubmit = async () => {
    if (!type || !message.trim()) { toast.error('Pilih tipe dan tulis pesan'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/tickets`, { type, message: message.trim(), screenshot_url: screenshotUrl || null }, { headers });
      toast.success('Feedback terkirim!');
      setType(''); setMessage(''); setScreenshotUrl('');
      setView('history'); fetchTickets();
    } catch (err) { toast.error('Gagal mengirim feedback'); }
    finally { setSending(false); }
  };

  const handleScreenshot = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await axios.post(`${API}/upload/image`, formData, { headers });
      if (res.data.url) { setScreenshotUrl(res.data.url); toast.success('Screenshot uploaded'); }
    } catch (err) { toast.error('Gagal upload screenshot'); }
  };

  const handleUserReply = async (ticketId) => {
    if (!replyText.trim() && !replyImageUrl) return;
    setReplying(true);
    try {
      await axios.post(`${API}/tickets/${ticketId}/user-reply`, { message: replyText.trim() || '[Gambar]', image_url: replyImageUrl || null }, { headers });
      setReplyText(''); setReplyImageUrl(''); fetchTickets();
    } catch (err) { toast.error('Gagal mengirim balasan'); }
    finally { setReplying(false); }
  };

  const handleReplyImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await axios.post(`${API}/upload/image`, formData, { headers });
      if (res.data.url) { setReplyImageUrl(res.data.url); toast.success('Gambar diupload'); }
    } catch (err) { toast.error('Gagal upload gambar'); }
  };

  if (!token) return null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A]" data-testid="feedback-page">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <div className="lg:ml-64">
        {/* Header */}
        <div className="bg-white dark:bg-zinc-900 border-b border-slate-200 dark:border-zinc-800 px-4 lg:px-6 py-4 sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-zinc-800 dark:bg-zinc-800" data-testid="sidebar-toggle">
              <Menu className="w-5 h-5 text-slate-600 dark:text-zinc-300" />
            </button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 flex items-center gap-2">
                <MessageCircleQuestion className="w-5 h-5 text-indigo-600 shrink-0 dark:text-indigo-400" /> Bantuan
              </h1>
              <p className="text-xs text-slate-500 hidden sm:block dark:text-zinc-400">Pusat bantuan, FAQ, dan kirim feedback</p>
            </div>
          </div>
        </div>

        {/* Content - 2 column layout */}
        <main className="p-4 lg:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

            {/* Left Column - Feedback Form (3/5) */}
            <div className="lg:col-span-3 space-y-4">
              {/* Tab Toggle */}
              <div className="flex border-b border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 rounded-t-xl px-1">
                <button
                  onClick={() => setView('form')}
                  className={`px-4 py-2.5 text-sm font-medium transition-colors ${view === 'form' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-slate-500 hover:text-slate-700'} dark:text-zinc-200`}
                  data-testid="feedback-tab-form"
                >
                  Kirim Feedback
                </button>
                <button
                  onClick={() => { setView('history'); fetchTickets(); }}
                  className={`px-4 py-2.5 text-sm font-medium transition-colors ${view === 'history' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-slate-500 hover:text-slate-700'} dark:text-zinc-200`}
                  data-testid="feedback-tab-history"
                >
                  Riwayat ({tickets.length})
                </button>
              </div>

              {/* Form View */}
              {view === 'form' && (
                <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-4 sm:p-6 space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300 mb-2">Tipe Feedback</label>
                    <div className="grid grid-cols-3 gap-2 sm:gap-3">
                      {TICKET_TYPES.map(t => (
                        <button
                          key={t.id}
                          onClick={() => setType(t.id)}
                          className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all ${type === t.id ? 'border-blue-500 bg-indigo-50 dark:bg-indigo-950/30' : 'border-slate-200 dark:border-zinc-800 hover:border-slate-300'}`}
                          data-testid={`feedback-type-${t.id}`}
                        >
                          <t.icon className={`w-5 h-5 ${t.color}`} />
                          <span className="text-xs font-medium text-slate-700 dark:text-zinc-300">{t.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300 mb-2">Pesan</label>
                    <textarea
                      value={message}
                      onChange={e => setMessage(e.target.value)}
                      placeholder="Jelaskan masalah atau saran Anda..."
                      rows={5}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-[#0A0A0A] text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      data-testid="feedback-message"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300 mb-2">Screenshot (opsional)</label>
                    {screenshotUrl ? (
                      <div className="relative inline-block">
                        <img src={screenshotUrl} alt="Preview" className="h-24 rounded-xl border" />
                        <button onClick={() => setScreenshotUrl('')} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">x</button>
                      </div>
                    ) : (
                      <label className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-slate-300 dark:border-zinc-700 text-slate-500 hover:border-blue-400 cursor-pointer transition-colors text-sm dark:text-zinc-400">
                        <ImageIcon className="w-4 h-4" /> Upload screenshot
                        <input type="file" accept="image/*" className="hidden" onChange={handleScreenshot} />
                      </label>
                    )}
                  </div>

                  <Button onClick={handleSubmit} disabled={sending || !type || !message.trim()} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="feedback-submit">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Kirim Feedback
                  </Button>
                </div>
              )}

              {/* History View */}
              {view === 'history' && (
                <div className="space-y-3">
                  {tickets.length === 0 ? (
                    <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-8 text-center">
                      <MessageCircleQuestion className="w-10 h-10 text-slate-500 dark:text-zinc-500 mx-auto mb-3" />
                      <p className="text-sm text-slate-500 dark:text-zinc-400">Belum ada tiket feedback</p>
                      <p className="text-xs text-slate-500 mt-1 dark:text-zinc-400">Kirim feedback pertama Anda di tab "Kirim Feedback"</p>
                    </div>
                  ) : tickets.map(ticket => (
                    <div key={ticket.id} className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 overflow-hidden">
                      <button
                        onClick={() => setExpandedTicket(expandedTicket === ticket.id ? null : ticket.id)}
                        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-zinc-800/50 transition-colors dark:bg-zinc-900"
                        data-testid={`ticket-${ticket.id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${STATUS_COLORS[ticket.status] || STATUS_COLORS.open}`}>
                            {STATUS_LABELS[ticket.status] || ticket.status}
                          </span>
                          <span className="text-sm text-slate-700 dark:text-zinc-300 truncate">{ticket.message?.substring(0, 60)}...</span>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0 text-slate-500 dark:text-zinc-400">
                          <Clock className="w-3.5 h-3.5" />
                          <span className="text-xs">{new Date(ticket.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })}</span>
                        </div>
                      </button>

                      {expandedTicket === ticket.id && (
                        <div className="px-4 pb-4 border-t border-slate-100 dark:border-zinc-800 pt-3 space-y-3">
                          <p className="text-sm text-slate-600 dark:text-zinc-400">{ticket.message}</p>
                          {ticket.screenshot_url && (
                            <img src={ticket.screenshot_url} alt="Screenshot" className="rounded-lg border max-h-40 object-cover cursor-pointer" onClick={() => window.open(ticket.screenshot_url, '_blank')} />
                          )}

                          {ticket.replies?.length > 0 && (
                            <div className="space-y-2 mt-3">
                              {ticket.replies.map((reply, idx) => (
                                <div key={idx} className={`p-3 rounded-lg text-sm ${reply.sender === 'admin' ? 'bg-indigo-50 dark:bg-indigo-950/30 border-l-2 border-blue-500' : 'bg-slate-50 dark:bg-zinc-800/50'}`}>
                                  <span className="text-xs font-medium text-slate-500 dark:text-zinc-400">{reply.sender === 'admin' ? 'Admin' : 'Anda'}</span>
                                  <p className="text-slate-700 dark:text-zinc-300 mt-0.5">{reply.message}</p>
                                  {reply.image_url && <img src={reply.image_url} alt="" className="mt-2 rounded-lg border max-h-32 object-cover cursor-pointer" onClick={() => window.open(reply.image_url, '_blank')} />}
                                  <p className="text-xs text-slate-500 mt-1 dark:text-zinc-400">{new Date(reply.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                                </div>
                              ))}
                            </div>
                          )}

                          {ticket.status !== 'resolved' ? (
                            <div className="space-y-2 pt-2">
                              {replyImageUrl && (
                                <div className="relative inline-block">
                                  <img src={replyImageUrl} alt="Preview" className="h-20 rounded-lg border" />
                                  <button onClick={() => setReplyImageUrl('')} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs">x</button>
                                </div>
                              )}
                              <div className="flex gap-2">
                                <input value={replyText} onChange={e => setReplyText(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleUserReply(ticket.id)} placeholder="Tulis balasan..." className="flex-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-zinc-800 bg-white dark:bg-[#0A0A0A] text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" data-testid="feedback-reply-input" />
                                <label className="p-2 rounded-lg border border-slate-200 dark:border-zinc-800 hover:bg-slate-50 cursor-pointer shrink-0 flex items-center dark:bg-zinc-900">
                                  <ImageIcon className="w-4 h-4 text-slate-500 dark:text-zinc-400" />
                                  <input type="file" accept="image/*" className="hidden" onChange={handleReplyImage} />
                                </label>
                                <Button size="sm" onClick={() => handleUserReply(ticket.id)} disabled={replying || (!replyText.trim() && !replyImageUrl)} className="bg-indigo-600 hover:bg-indigo-700 px-3 shrink-0" data-testid="feedback-reply-send">
                                  {replying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <p className="text-xs text-slate-500 text-center py-2 bg-slate-50 dark:bg-zinc-800/30 rounded-lg dark:text-zinc-400">Tiket sudah resolved</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right Column - FAQ & Contact (2/5) */}
            <div className="lg:col-span-2 space-y-4">
              {/* FAQ */}
              <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-4 sm:p-5">
                <h2 className="text-sm font-semibold text-slate-900 dark:text-zinc-50 mb-4 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  Pertanyaan Umum (FAQ)
                </h2>
                <div className="space-y-1">
                  {FAQ_ITEMS.map((item, idx) => (
                    <div key={idx} className="border-b border-slate-100 dark:border-zinc-800/50 last:border-0">
                      <button
                        onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                        className="w-full flex items-start justify-between gap-2 py-3 text-left group"
                        data-testid={`faq-item-${idx}`}
                      >
                        <span className="text-sm text-slate-700 dark:text-zinc-300 group-hover:text-indigo-600 dark:hover:text-indigo-400 dark:group-hover:text-indigo-400 transition-colors">{item.q}</span>
                        <ChevronDown className={`w-4 h-4 text-slate-500 shrink-0 mt-0.5 transition-transform ${expandedFaq === idx ? 'rotate-180' : ''} dark:text-zinc-400`} />
                      </button>
                      {expandedFaq === idx && (
                        <p className="text-xs text-slate-500 dark:text-zinc-400 pb-3 leading-relaxed">{item.a}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact Support */}
              <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-4 sm:p-5">
                <h2 className="text-sm font-semibold text-slate-900 dark:text-zinc-50 mb-4 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  Hubungi Kami
                </h2>
                <div className="space-y-3">
                  <a href="mailto:support@nerava.id" className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-zinc-800 hover:border-indigo-300 hover:bg-indigo-50/50 dark:hover:bg-blue-900/10 transition-colors group dark:bg-indigo-950/40" data-testid="contact-email">
                    <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-950/40 flex items-center justify-center shrink-0">
                      <Mail className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-700 dark:text-zinc-300 group-hover:text-indigo-600 dark:hover:text-indigo-400 dark:group-hover:text-indigo-400">Email Support</p>
                      <p className="text-xs text-slate-500 truncate dark:text-zinc-400">support@nerava.id</p>
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 text-slate-500 shrink-0 ml-auto dark:text-zinc-400" />
                  </a>

                  <a href="https://wa.me/6285121288598" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-zinc-800 hover:border-green-300 hover:bg-green-50/50 dark:hover:bg-green-900/10 transition-colors group dark:bg-green-950/40" data-testid="contact-whatsapp">
                    <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center shrink-0">
                      <MessageSquare className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-700 dark:text-zinc-300 group-hover:text-green-600 dark:hover:text-green-400 dark:group-hover:text-green-400">WhatsApp</p>
                      <p className="text-xs text-slate-500 truncate dark:text-zinc-400">Chat langsung dengan tim kami</p>
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 text-slate-500 shrink-0 ml-auto dark:text-zinc-400" />
                  </a>
                </div>
              </div>

              {/* Quick Tips */}
              <div className="bg-indigo-50 dark:bg-indigo-950/30 rounded-xl border border-indigo-200 dark:border-indigo-800 p-4 sm:p-5">
                <h2 className="text-sm font-semibold text-blue-900 dark:text-indigo-200 mb-3 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4" />
                  Tips Kirim Feedback
                </h2>
                <ul className="space-y-2 text-xs text-indigo-700 dark:text-indigo-300 leading-relaxed">
                  <li className="flex items-start gap-2">
                    <span className="w-1 h-1 rounded-full bg-indigo-500 shrink-0 mt-1.5" />
                    Sertakan screenshot untuk mempercepat penanganan bug
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1 h-1 rounded-full bg-indigo-500 shrink-0 mt-1.5" />
                    Jelaskan langkah-langkah yang dilakukan sebelum error terjadi
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1 h-1 rounded-full bg-indigo-500 shrink-0 mt-1.5" />
                    Untuk fitur baru, jelaskan kebutuhan bisnis dan use case Anda
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
